import axios from 'axios';
import { PreApprovalRequest, LenderJourneyResponse } from '../types/preApproval';

// API Base URL from environment variables
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// API endpoints
const CHECK_ELIGIBILITY_ENDPOINT = '/gateway/check-eligibility';
const PROCEED_TO_LENDER_ENDPOINT = '/gateway/proceed-to-lender';

// Determine if running in development mode
const IS_DEV_MODE = import.meta.env.MODE === 'development';

/**
 * Call the Gateway Backend which proxies to the pre-approval service
 * @param otpToken The OTP token received after verification (gateway partner token)
 * @param userData User data for pre-approval (name, mobile, etc.)
 * @returns Promise with the pre-approval response
 */
export const callPreApprovalService = async (
  otpToken: string,
  userData: Partial<PreApprovalRequest>
) => {
  // Log important debug info
  if (IS_DEV_MODE) {
    console.log('Gateway pre-approval API URL:', `${API_BASE_URL}${CHECK_ELIGIBILITY_ENDPOINT}`);
    console.log('Has Gateway Partner OTP token:', !!otpToken);
    console.log('User data for pre-approval:', userData);
  }

  // Make the API call with required authentication headers
  try {
    const headers = {
      'Authorization': `Bearer ${otpToken}`,
      'Content-Type': 'application/json'
    };

    const response = await axios.post(
      `${API_BASE_URL}${CHECK_ELIGIBILITY_ENDPOINT}`,
      userData,
      { headers }
    );

    return response.data;
  } catch (error: any) {
    console.error('Pre-approval service error:', error);
    if (error.response) {
      console.error('Error status:', error.response.status);
      console.error('Error data:', error.response.data);
    }
    throw error;
  }
};

/**
 * Process the "Proceed" action after pre-approval success
 * This updates the order with lender information and retrieves the redirection link
 * 
 * @param orderId The ID of the order to update
 * @param schemeSelected The selected scheme ID
 * @returns Promise with the redirection link and updated order info
 */
export const proceedToLenderJourney = async (
  orderId: string,
  schemeSelected: string
): Promise<LenderJourneyResponse['data']> => {
  try {
    // Extract auth token from sessionStorage
    const authToken = sessionStorage.getItem('authToken');

    if (!authToken) {
      throw new Error('Authentication required. Please login again.');
    }
    
    // Validate required fields
    if (!orderId) {
      throw new Error('Order ID is required');
    }

    // Prepare request body
    const requestBody = {
      orderId,
      schemeSelected: schemeSelected,
    };

    // Log request body for debugging
    if (IS_DEV_MODE) {
      console.log('Proceed to lender request body:', JSON.stringify(requestBody, null, 2));
    }

    // Make the API call with authentication
    const response = await axios.post(
      `${API_BASE_URL}${PROCEED_TO_LENDER_ENDPOINT}`,
      requestBody,
      {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.data?.success) {
      throw new Error(response.data?.message || 'Failed to process lender journey');
    }

    const responseData = response.data.data;

    // Update session storage with new orderId if available
    if (responseData?.orderId) {
      try {
        const transactionDataStr = sessionStorage.getItem('transactionData');
        if (transactionDataStr) {
          const transactionData = JSON.parse(transactionDataStr);
          transactionData.orderId = responseData.orderId;
          sessionStorage.setItem('transactionData', JSON.stringify(transactionData));
        }
        sessionStorage.setItem('orderId', responseData.orderId);
        console.log('Updated transaction data with latest order ID:', responseData.orderId);
      } catch (e) {
        console.error('Error updating transaction data with latest order ID:', e);
      }
    }

    return responseData;
  } catch (error: any) {
    console.error('Error proceeding to lender journey:', error);

    if (error.response) {
      console.error('Server error details:', error.response.data);
    }

    throw new Error(
      error.response?.data?.message ||
      error.message ||
      'An error occurred while processing your request'
    );
  }
}; 